import type {
  Candle,
  DataAvailabilityInterval,
  Denom,
  PairDetail,
  PriceWindowSnapshot,
  PulseItem,
  Resolution,
  TokenSummary,
  TradeSummary,
  WindowHead,
  WindowTradeEvent,
} from '../api/types'
import { bucketSeconds, bucketStart, candleTradePrice, mergeTradeIntoCandles } from './candles'
import { isNonTonQuote, tradeRowUsdRate } from './tonQuote'
import { windowHeadTape } from './windowHead'

export interface TradeWindow {
  events: WindowTradeEvent[]
  buys: number
  sells: number
  txns: number
  volumeNative: number
  recentVolumeNative: number
  /** Priced trades of the recent quarter in USD (the Trending numerator). */
  recentVolumeUSD: number
  /** FLOOR over priced trades; the matching *Unpriced count is the gap. */
  volumeUSD: number
  buyUSD: number
  sellUSD: number
  volumeUSDUnpriced: number
  buyUSDUnpriced: number
  sellUSDUnpriced: number
  member: boolean
  priceWindow: PriceWindowSnapshot | null
  priceChangePct: number | null
  sparkline: Array<{ ts: number; price: number }>
}

export function windowTradeIdentity(
  event: Pick<WindowTradeEvent, 'pool_addr' | 'ts' | 'lt' | 'lt_key' | 'side' | 'quote_native'>
): string {
  return JSON.stringify([
    event.pool_addr ?? '',
    event.ts,
    windowTradeLTKey(event),
    event.side,
    event.quote_native,
  ])
}

export function compareWindowTrades(left: WindowTradeEvent, right: WindowTradeEvent): number {
  if (left.ts !== right.ts) return left.ts - right.ts
  const lt = compareWindowTradeLT(left, right)
  if (lt !== 0) return lt
  return left.tx_hash.localeCompare(right.tx_hash)
}

function windowTradeLTKey(event: Pick<WindowTradeEvent, 'lt' | 'lt_key'>): string {
  if (event.lt_key != null) return event.lt_key
  return typeof event.lt === 'number' && Number.isFinite(event.lt)
    ? String(Math.trunc(event.lt))
    : '0'
}

function compareWindowTradeLT(
  left: Pick<WindowTradeEvent, 'lt' | 'lt_key'>,
  right: Pick<WindowTradeEvent, 'lt' | 'lt_key'>
): number {
  const a = windowTradeLTKey(left)
  const b = windowTradeLTKey(right)
  if (a === b) return 0
  return a.length === b.length ? (a < b ? -1 : 1) : a.length - b.length
}

const orderedWindowTapeCache = new WeakMap<
  readonly WindowTradeEvent[],
  readonly WindowTradeEvent[]
>()

// A full Pair tape can contain several thousand factual events. It stays
// immutable, but a live append must not make every clock tick sort/reduce that
// history again. This is a derived index keyed by tape identity, not a second
// source of truth: every number below is reconstructed exclusively from the
// events stored in its chunks.
const WINDOW_INDEX_CHUNK_SIZE = 64

interface WindowTapeChunk {
  events: readonly WindowTradeEvent[]
  ts: readonly number[]
  buys: readonly number[]
  sells: readonly number[]
  native: readonly number[]
  usd: readonly number[]
  usdUnknown: readonly number[]
  buyUsd: readonly number[]
  buyUsdUnknown: readonly number[]
  sellUsd: readonly number[]
  sellUsdUnknown: readonly number[]
}

interface WindowTapeIndex {
  chunks: readonly WindowTapeChunk[]
  parent?: WindowTapeIndex
  appendedMinTS?: number
}

interface WindowTapeAppend {
  previous: readonly WindowTradeEvent[]
  events: readonly WindowTradeEvent[]
}

const windowTapeIndexCache = new WeakMap<readonly WindowTradeEvent[], WindowTapeIndex>()
const windowTapeAppendCache = new WeakMap<readonly WindowTradeEvent[], WindowTapeAppend>()
const agedPriceWindowCache = new WeakMap<
  PriceWindowSnapshot,
  WeakMap<WindowTapeIndex, Map<number, PriceWindowSnapshot>>
>()

function orderedWindowTrades(tape: readonly WindowTradeEvent[]): readonly WindowTradeEvent[] {
  const cached = orderedWindowTapeCache.get(tape)
  if (cached) return cached
  const ordered = [...tape].sort(compareWindowTrades)
  orderedWindowTapeCache.set(tape, ordered)
  orderedWindowTapeCache.set(ordered, ordered)
  return ordered
}

/** Keeps the factual tape immutable and lossless. Expiry is a render concern. */
export function mergeWindowTrades(
  previous: readonly WindowTradeEvent[],
  incoming: readonly WindowTradeEvent[]
): WindowTradeEvent[] {
  if (incoming.length === 0) return previous as WindowTradeEvent[]
  if (previous.length === 0) {
    const ordered = [...incoming].sort(compareWindowTrades)
    orderedWindowTapeCache.set(ordered, ordered)
    return ordered
  }
  // Live delivery is monotonic by (ts, lt). The immutable array copy is linear,
  // but it avoids rebuilding a Map and sorting the full 24h tape on every trade
  // (the visible freeze). Equal-cursor replay still falls through to the exact
  // dedupe path below.
  const firstIncoming = incoming[0] as WindowTradeEvent
  const lastPrevious = previous[previous.length - 1] as WindowTradeEvent
  let appendOnly =
    compareWindowTrades(lastPrevious, firstIncoming) < 0 &&
    windowTradeIdentity(lastPrevious) !== windowTradeIdentity(firstIncoming)
  for (let index = 1; appendOnly && index < incoming.length; index++) {
    appendOnly =
      compareWindowTrades(
        incoming[index - 1] as WindowTradeEvent,
        incoming[index] as WindowTradeEvent
      ) < 0
  }
  if (appendOnly) {
    const appended = [...previous, ...incoming]
    orderedWindowTapeCache.set(appended, appended)
    // Keep the append provenance beside the immutable tape. If the previous
    // tape was already indexed, the next frame extends only its small tail
    // chunk instead of rebuilding an O(history) prefix index.
    windowTapeAppendCache.set(appended, { previous, events: incoming })
    return appended
  }
  const byIdentity = new Map<string, WindowTradeEvent>()
  for (const event of previous) byIdentity.set(windowTradeIdentity(event), event)
  for (const event of incoming) byIdentity.set(windowTradeIdentity(event), event)
  const merged = Array.from(byIdentity.values()).sort(compareWindowTrades)
  orderedWindowTapeCache.set(merged, merged)
  return merged
}

export function windowEventFromTrade(trade: TradeSummary): WindowTradeEvent {
  const cached = tradeWindowEventCache.get(trade)
  if (cached) return cached
  const candlePrice = candleTradePrice(trade)
  const usdRate = tradeUSDRate(trade)
  const quoteUSD = tradeQuoteUSD(trade, usdRate)
  const event: WindowTradeEvent = {
    ts: trade.ts,
    lt: trade.lt,
    lt_key: trade.lt_key,
    tx_hash: trade.tx_hash,
    side: trade.side,
    pool_addr: trade.pool_addr,
    base_token: trade.base_token,
    base_amount: trade.base_amount,
    quote_native: Math.max(0, trade.quote_amount),
    quote_usd: quoteUSD,
    price: trade.price,
    native_price: candlePrice,
    usd_price: tradeCandleUSDPrice(candlePrice, usdRate),
    mcap_usd: finiteNonNegative(trade.mcap_usd) ? trade.mcap_usd : null,
    fdv_usd: finiteNonNegative(trade.fdv_usd) ? trade.fdv_usd : null,
    liquidity_usd: finiteNonNegative(trade.liquidity_usd) ? trade.liquidity_usd : null,
  }
  tradeWindowEventCache.set(trade, event)
  return event
}

export function windowEventsToTrades(events: readonly WindowTradeEvent[]): TradeSummary[] {
  return events.map((event) => {
    const cached = windowEventTradeCache.get(event)
    if (cached) return cached
    const trade: TradeSummary = {
      ts: event.ts,
      lt: event.lt,
      tx_hash: event.tx_hash,
      side: event.side === 'buy' || event.side === 'sell' ? event.side : 'unknown',
      base_amount: event.base_amount ?? 0,
      quote_amount: event.quote_native,
      price: finitePositive(event.price) ? (event.price as number) : (event.native_price ?? 0),
      spot_price: event.native_price,
      price_usd: event.usd_price,
      // quote_usd is quote × the trade's own stamped rate, so its ratio to the
      // quote leg IS that rate. The feed-row USD total must use it — the
      // usd_price/price ratio mixes the candle basis with the raw fill and is
      // off by the fee. Neither price field moves.
      ton_usd_rate:
        typeof event.quote_usd === 'number' &&
        Number.isFinite(event.quote_usd) &&
        event.quote_usd >= 0 &&
        event.quote_native > 0
          ? event.quote_usd / event.quote_native
          : undefined,
      mcap_usd: event.mcap_usd,
      fdv_usd: event.fdv_usd,
      liquidity_usd: event.liquidity_usd,
      trader_addr: '',
      pool_addr: event.pool_addr,
      base_token: event.base_token,
    }
    windowEventTradeCache.set(event, trade)
    return trade
  })
}

// Pair/window objects are immutable throughout the live reducers. Weak memo
// identity lets the chart keep the already-projected prefix when one factual
// event is appended, without introducing a second mutable source of truth.
const tradeWindowEventCache = new WeakMap<TradeSummary, WindowTradeEvent>()
const windowEventTradeCache = new WeakMap<WindowTradeEvent, TradeSummary>()

/** Return the strictly newer suffix when `next` is the same exact ordered tape
 * plus appended events. Any replacement, removal, or out-of-order insertion
 * returns null so the caller performs a full reconstruction. */
export function appendedWindowTrades(
  previous: readonly WindowTradeEvent[],
  next: readonly WindowTradeEvent[]
): WindowTradeEvent[] | null {
  if (next.length < previous.length) return null
  for (let index = 0; index < previous.length; index++) {
    if (previous[index] !== next[index]) return null
  }
  const suffix = next.slice(previous.length)
  if (suffix.length === 0 || previous.length === 0) return suffix
  const previousLast = previous[previous.length - 1] as WindowTradeEvent
  if (compareWindowTrades(previousLast, suffix[0] as WindowTradeEvent) >= 0) return null
  return suffix
}

export function deriveTradeWindow(
  tape: readonly WindowTradeEvent[],
  nowSec: number,
  windowSec: number,
  seed?: PriceWindowSnapshot | null
): TradeWindow {
  return deriveTradeWindowFromIndex(windowTapeIndex(tape), nowSec, windowSec, seed)
}

function deriveTradeWindowFromIndex(
  index: WindowTapeIndex,
  nowSec: number,
  windowSec: number,
  seed?: PriceWindowSnapshot | null
): TradeWindow {
  const cutoff = nowSec - windowSec
  const active = windowEvents(index, cutoff, nowSec)
  const summary = summarizeIndexedWindow(index, cutoff, nowSec, nowSec - windowSec / 4)
  const priceWindow = agePriceWindowIndexed(seed, index, nowSec, windowSec)
  const sparkline = windowSparkline(active, priceWindow, nowSec, windowSec)
  return {
    events: active,
    ...summary,
    member: active.length > 0,
    priceWindow,
    priceChangePct: priceWindow?.pct ?? null,
    sparkline,
  }
}

function windowTapeIndex(tape: readonly WindowTradeEvent[]): WindowTapeIndex {
  const ordered = orderedWindowTrades(tape)
  const cached = windowTapeIndexCache.get(ordered)
  if (cached) return cached

  const appended = windowTapeAppendCache.get(ordered)
  if (appended) {
    const parent = windowTapeIndex(appended.previous)
    const next = appendWindowTapeIndex(parent, appended.events)
    windowTapeIndexCache.set(ordered, next)
    return next
  }

  const chunks: WindowTapeChunk[] = []
  for (let from = 0; from < ordered.length; from += WINDOW_INDEX_CHUNK_SIZE) {
    chunks.push(buildWindowTapeChunk(ordered.slice(from, from + WINDOW_INDEX_CHUNK_SIZE)))
  }
  const index = { chunks }
  windowTapeIndexCache.set(ordered, index)
  return index
}

function appendWindowTapeIndex(
  parent: WindowTapeIndex,
  appended: readonly WindowTradeEvent[]
): WindowTapeIndex {
  if (appended.length === 0) return parent
  const chunks = [...parent.chunks]
  let offset = 0
  const tail = chunks.at(-1)
  if (tail && tail.events.length < WINDOW_INDEX_CHUNK_SIZE) {
    const take = Math.min(WINDOW_INDEX_CHUNK_SIZE - tail.events.length, appended.length)
    chunks[chunks.length - 1] = buildWindowTapeChunk([...tail.events, ...appended.slice(0, take)])
    offset = take
  }
  for (; offset < appended.length; offset += WINDOW_INDEX_CHUNK_SIZE) {
    chunks.push(buildWindowTapeChunk(appended.slice(offset, offset + WINDOW_INDEX_CHUNK_SIZE)))
  }
  return {
    chunks,
    parent,
    appendedMinTS: appended.reduce(
      (minimum, event) => Math.min(minimum, event.ts),
      Number.POSITIVE_INFINITY
    ),
  }
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: one factual event updates all prefix columns together
function buildWindowTapeChunk(events: readonly WindowTradeEvent[]): WindowTapeChunk {
  const ts: number[] = []
  const buys = [0]
  const sells = [0]
  const native = [0]
  const usd = [0]
  const usdUnknown = [0]
  const buyUsd = [0]
  const buyUsdUnknown = [0]
  const sellUsd = [0]
  const sellUsdUnknown = [0]
  for (const event of events) {
    const quoteUSD = finiteNonNegative(event.quote_usd) ? (event.quote_usd as number) : 0
    const unknownUSD = finiteNonNegative(event.quote_usd) ? 0 : 1
    ts.push(event.ts)
    buys.push(buys.at(-1)! + (event.side === 'buy' ? 1 : 0))
    sells.push(sells.at(-1)! + (event.side === 'sell' ? 1 : 0))
    native.push(native.at(-1)! + (finiteNonNegative(event.quote_native) ? event.quote_native : 0))
    usd.push(usd.at(-1)! + quoteUSD)
    usdUnknown.push(usdUnknown.at(-1)! + unknownUSD)
    buyUsd.push(buyUsd.at(-1)! + (event.side === 'buy' ? quoteUSD : 0))
    buyUsdUnknown.push(buyUsdUnknown.at(-1)! + (event.side === 'buy' ? unknownUSD : 0))
    sellUsd.push(sellUsd.at(-1)! + (event.side === 'sell' ? quoteUSD : 0))
    sellUsdUnknown.push(sellUsdUnknown.at(-1)! + (event.side === 'sell' ? unknownUSD : 0))
  }
  return {
    events,
    ts,
    buys,
    sells,
    native,
    usd,
    usdUnknown,
    buyUsd,
    buyUsdUnknown,
    sellUsd,
    sellUsdUnknown,
  }
}

function firstAfter(ts: readonly number[], value: number): number {
  let low = 0
  let high = ts.length
  while (low < high) {
    const middle = (low + high) >>> 1
    if ((ts[middle] as number) <= value) low = middle + 1
    else high = middle
  }
  return low
}

function prefixDelta(prefix: readonly number[], from: number, to: number): number {
  return (prefix[to] as number) - (prefix[from] as number)
}

interface IndexedSummary {
  buys: number
  sells: number
  txns: number
  volumeNative: number
  recentVolumeNative: number
  /** Priced trades of the recent quarter in USD (the Trending numerator). */
  recentVolumeUSD: number
  /** FLOOR over priced trades; the matching *Unpriced count is the gap. */
  volumeUSD: number
  buyUSD: number
  sellUSD: number
  volumeUSDUnpriced: number
  buyUSDUnpriced: number
  sellUSDUnpriced: number
}

function summarizeIndexedWindow(
  index: WindowTapeIndex,
  cutoff: number,
  nowSec: number,
  recentCutoff: number
): IndexedSummary {
  let buys = 0
  let sells = 0
  let txns = 0
  let native = 0
  let recentNative = 0
  let recentUSD = 0
  let usd = 0
  let usdUnknown = 0
  let buyUSD = 0
  let buyUSDUnknown = 0
  let sellUSD = 0
  let sellUSDUnknown = 0
  for (const chunk of index.chunks) {
    const from = firstAfter(chunk.ts, cutoff)
    const to = firstAfter(chunk.ts, nowSec)
    if (from === to) continue
    const recentFrom = Math.min(to, firstAfter(chunk.ts, recentCutoff))
    buys += prefixDelta(chunk.buys, from, to)
    sells += prefixDelta(chunk.sells, from, to)
    txns += to - from
    native += prefixDelta(chunk.native, from, to)
    recentNative += prefixDelta(chunk.native, recentFrom, to)
    usd += prefixDelta(chunk.usd, from, to)
    recentUSD += prefixDelta(chunk.usd, recentFrom, to)
    usdUnknown += prefixDelta(chunk.usdUnknown, from, to)
    buyUSD += prefixDelta(chunk.buyUsd, from, to)
    buyUSDUnknown += prefixDelta(chunk.buyUsdUnknown, from, to)
    sellUSD += prefixDelta(chunk.sellUsd, from, to)
    sellUSDUnknown += prefixDelta(chunk.sellUsdUnknown, from, to)
  }
  return {
    buys,
    sells,
    txns,
    volumeNative: native,
    recentVolumeNative: recentNative,
    recentVolumeUSD: recentUSD,
    // FLOOR over priced trades, with the gap counted — the same honest
    // semantics the REST windows carry (value* + txns_*_unpriced). The old
    // all-or-nothing null blanked a whole window for one unpriced trade,
    // which every fast-lane pending frame now is for ~a second (stage-3 B1):
    // the window flickered to "—" on every live trade.
    volumeUSD: usd,
    buyUSD,
    sellUSD,
    volumeUSDUnpriced: usdUnknown,
    buyUSDUnpriced: buyUSDUnknown,
    sellUSDUnpriced: sellUSDUnknown,
  }
}

/** Window counts and volume of a capped row: its expiry head plus the retained
 *  tape (tail + live trades), plus the head's constant rest. Exactly the
 *  server's reduction at nowSec while the head is current (lib/windowHead.ts). */
export interface HeadWindowCounts {
  txns: number
  buys: number
  sells: number
  volumeNative: number
  recentVolumeNative: number
  recentVolumeUSD: number
  volumeUSD: number
  volumeUSDUnpriced: number
}

export function headWindowCounts(
  head: WindowHead,
  tape: readonly WindowTradeEvent[],
  nowSec: number,
  windowSec: number
): HeadWindowCounts {
  const index = windowTapeIndex(windowHeadTape(head, tape))
  const summary = summarizeIndexedWindow(index, nowSec - windowSec, nowSec, nowSec - windowSec / 4)
  const rest = head.rest
  return {
    txns: summary.txns + (rest?.txns ?? 0),
    buys: summary.buys + (rest?.buys ?? 0),
    sells: summary.sells + (rest?.sells ?? 0),
    volumeNative: summary.volumeNative + (rest?.quote_native ?? 0),
    recentVolumeNative: summary.recentVolumeNative + (rest?.recent_native ?? 0),
    recentVolumeUSD: summary.recentVolumeUSD + (rest?.recent_usd ?? 0),
    volumeUSD: summary.volumeUSD + (rest?.quote_usd ?? 0),
    volumeUSDUnpriced: summary.volumeUSDUnpriced + (rest?.usd_unpriced ?? 0),
  }
}

/** The head belongs to the window being projected (a Discover window switch
 *  is a different request, but never trust a mismatched head).
 *
 *  Past head.until the head is still used, deliberately: its events and the
 *  tape keep aging exactly, and only the rest events whose membership changes
 *  after until are held constant — the error grows from 0 by about the row's
 *  trade rate until the re-read (headReloadDue) lands. Falling back to the
 *  stored snapshot count instead would re-add every head event that already
 *  expired since the snapshot (up to ListWindowHeadCap = 512 at once): the
 *  figure would jump up at until and back down on the re-read. */
function usableHead(
  row: { window_events_tail?: boolean; window_head?: WindowHead | null },
  windowSec: number
): WindowHead | null {
  const head = row.window_head
  return row.window_events_tail && head && head.window_sec === windowSec ? head : null
}

function windowEvents(index: WindowTapeIndex, cutoff: number, nowSec: number): WindowTradeEvent[] {
  const out: WindowTradeEvent[] = []
  for (const chunk of index.chunks) {
    const from = firstAfter(chunk.ts, cutoff)
    const to = firstAfter(chunk.ts, nowSec)
    if (from !== to) out.push(...chunk.events.slice(from, to))
  }
  return out
}

// The mini-chart and the percent label are one projection of one exact window.
// Trades strictly inside (now-window, now] draw the factual shape; the proven
// carry-forward baseline is pinned at the left boundary and the proven current
// price at now. Without these two anchors a token can rise over 24h while the
// locally rebuilt series starts after the rise and visually slopes down.
function windowSparkline(
  active: readonly WindowTradeEvent[],
  snapshot: PriceWindowSnapshot | null,
  nowSec: number,
  windowSec: number,
  seedSeries?: ReadonlyArray<{ ts: number; price: number }>
): Array<{ ts: number; price: number }> {
  const points: Array<{ ts: number; price: number }> = []
  const nativeBasis = snapshot?.pct_denom === 'native'
  const currentNative = snapshot?.current_native_price
  const currentUSD = snapshot?.current_price
  const nativeToUSD =
    nativeBasis && finitePositive(currentNative) && finitePositive(currentUSD)
      ? (currentUSD as number) / (currentNative as number)
      : null
  // A native-basis change must not be drawn from event-stamped USD points:
  // historical FX movement could reverse the line direction. Mark the whole
  // native tape with one current FX rate, or omit the directed series when that
  // mark is unproven.
  if (nativeBasis && !finitePositive(nativeToUSD)) return points
  const markedPrice = (usd: number | null | undefined, native: number | null | undefined) =>
    nativeBasis && nativeToUSD != null
      ? finitePositive(native)
        ? (native as number) * nativeToUSD
        : null
      : usd
  const push = (ts: number, price: number | null | undefined): void => {
    if (!Number.isFinite(ts) || !finitePositive(price)) return
    const point = { ts, price: price as number }
    // `ts` is second-granular; the exact tape is already ordered by (ts, lt).
    // Equal timestamps are distinct factual observations and must remain
    // distinct here, otherwise a same-second low/rebound disappears before
    // width-aware rendering and hover ever see it.
    points.push(point)
  }

  if (
    snapshot &&
    (snapshot.status === 'ok' ||
      snapshot.status === 'since_birth' ||
      snapshot.status === 'since_first_trade' ||
      snapshot.status === 'since_recovery') &&
    finitePositive(markedPrice(snapshot.baseline_price, snapshot.baseline_native_price))
  ) {
    const source = parseSeconds(snapshot.baseline_source_ts)
    const baselineTS =
      (snapshot.status === 'since_birth' ||
        snapshot.status === 'since_first_trade' ||
        snapshot.status === 'since_recovery') &&
      source != null
        ? source
        : nowSec - windowSec
    push(baselineTS, markedPrice(snapshot.baseline_price, snapshot.baseline_native_price))
  }
  // A server-capped tape (window_events_tail) is only the live edge of the
  // window: seed the shape from the REST/MV series up to the first retained
  // event, then let the exact tape own the edge. Seed points are already USD
  // (MV closes, FX-normalized server-side for native-basis rows), so they are
  // pushed raw, never re-marked.
  for (const point of seedSparklinePoints(seedSeries, active, nowSec, windowSec)) {
    push(point.ts, point.price)
  }
  for (const event of active) push(event.ts, markedPrice(event.usd_price, event.native_price))
  if (snapshot) push(nowSec, markedPrice(snapshot.current_price, snapshot.current_native_price))
  return points
}

// The MV-seed points of the window shape that predate the retained tape edge.
// The tape owns the edge itself, so anything at or after the first retained
// event (or outside the sliding window) is left out.
function seedSparklinePoints(
  seedSeries: ReadonlyArray<{ ts: number; price: number }> | undefined,
  active: readonly WindowTradeEvent[],
  nowSec: number,
  windowSec: number
): Array<{ ts: number; price: number }> {
  if (!seedSeries?.length) return []
  const edge = active.length > 0 ? (active[0] as WindowTradeEvent).ts : Number.POSITIVE_INFINITY
  const out: Array<{ ts: number; price: number }> = []
  for (const point of seedSeries) {
    if (point.ts <= nowSec - windowSec || point.ts >= edge || point.ts > nowSec) continue
    out.push(point)
  }
  return out
}

// Same calculation as agePriceWindow, but the factual searches are bounded to
// the moved boundary and one predecessor lookup. The old implementation
// replayed the entire 24h tape to rediscover the latest price on every tick.
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: boundary, primary price, and companion price must advance as one atomic snapshot
function agePriceWindowIndexed(
  seed: PriceWindowSnapshot | null | undefined,
  index: WindowTapeIndex,
  nowSec: number,
  windowSec: number
): PriceWindowSnapshot | null {
  if (!seed) return null
  const cached = readAgedPriceWindow(seed, index, windowSec, nowSec)
  const cachedAsOf = cached ? parseSeconds(cached.as_of) : null
  // Pair's render state intentionally stays immutable. Keep the latest
  // derived boundary beside its immutable source snapshot so a long-open page
  // advances only over newly-expired events instead of rescanning from seed.
  const previous: PriceWindowSnapshot =
    cached != null && cachedAsOf != null && cachedAsOf <= nowSec ? cached : seed
  const next: PriceWindowSnapshot = { ...previous, window_seconds: windowSec, as_of: iso(nowSec) }
  const seedAsOf = parseSeconds(previous.as_of)
  const seedCutoff = seedAsOf == null ? nowSec - windowSec : seedAsOf - windowSec
  const cutoff = nowSec - windowSec
  if (cutoff > seedCutoff) {
    forEachWindowEvent(index, seedCutoff, cutoff, (event) => applyBoundaryTrade(next, event))
  }
  // Lifecycle baselines are special only while their factual source is inside
  // the selected window. A capped tape may not retain the event that moves the
  // boundary, so advance the status from the immutable source timestamp too.
  // Keep baseline_source_ts unchanged: it remains provenance, while an `ok`
  // sparkline anchors the same price at the rolling cutoff.
  const baselineSource = parseSeconds(
    next.status === 'since_recovery' ? next.effective_start : next.baseline_source_ts
  )
  if (
    (next.status === 'since_birth' ||
      next.status === 'since_first_trade' ||
      next.status === 'since_recovery') &&
    baselineSource != null &&
    cutoff >= baselineSource
  ) {
    next.status = 'ok'
  }
  const current = latestPricedWindowEvent(index, nowSec, next)
  if (current) {
    applyCurrentTrade(next, current)
    // If the newest native observation lacks USD, take only the FX rate from
    // the nearest earlier dual-denomination event and re-mark the new native
    // current. Never retain the companion's older USD absolute as current.
    const companion = latestCompanionWindowEvent(index, nowSec, current, next)
    if (companion) {
      if (
        next.pct_denom === 'native' &&
        finitePositive(companion.usd_price) &&
        finitePositive(companion.native_price) &&
        finitePositive(next.current_native_price)
      ) {
        const currentFX = (companion.usd_price as number) / (companion.native_price as number)
        next.current_price = (next.current_native_price as number) * currentFX
      }
      if (next.pct_denom !== 'native' && finitePositive(companion.native_price)) {
        next.current_native_price = companion.native_price as number
      }
    }
  }
  const { baseline, current: currentPrice } = windowBasisPrices(next)
  next.pct = priceChange(baseline, currentPrice)
  if (currentPrice == null) next.status = 'no_price'
  else if (baseline == null) next.status = 'no_baseline'
  else if (
    next.status !== 'since_birth' &&
    next.status !== 'since_first_trade' &&
    next.status !== 'since_recovery'
  )
    next.status = 'ok'
  let byIndex = agedPriceWindowCache.get(seed)
  if (!byIndex) {
    byIndex = new WeakMap()
    agedPriceWindowCache.set(seed, byIndex)
  }
  let byWindow = byIndex.get(index)
  if (!byWindow) {
    byWindow = new Map()
    byIndex.set(index, byWindow)
  }
  byWindow.set(windowSec, next)
  return next
}

function readAgedPriceWindow(
  seed: PriceWindowSnapshot,
  index: WindowTapeIndex,
  windowSec: number,
  nowSec: number
): PriceWindowSnapshot | undefined {
  const byIndex = agedPriceWindowCache.get(seed)
  let appendedMinTS = Number.POSITIVE_INFINITY
  for (
    let candidate: WindowTapeIndex | undefined = index;
    candidate;
    candidate = candidate.parent
  ) {
    const snapshot = byIndex?.get(candidate)?.get(windowSec)
    const asOf = snapshot ? parseSeconds(snapshot.as_of) : null
    const cutoff = asOf == null ? Number.POSITIVE_INFINITY : asOf - windowSec
    if (snapshot && asOf != null && asOf <= nowSec && appendedMinTS > cutoff) return snapshot
    if (candidate.appendedMinTS != null) {
      appendedMinTS = Math.min(appendedMinTS, candidate.appendedMinTS)
    }
  }
  return undefined
}

function forEachWindowEvent(
  index: WindowTapeIndex,
  after: number,
  through: number,
  visit: (event: WindowTradeEvent) => void
): void {
  for (const chunk of index.chunks) {
    const from = firstAfter(chunk.ts, after)
    const to = firstAfter(chunk.ts, through)
    for (let eventIndex = from; eventIndex < to; eventIndex++) {
      visit(chunk.events[eventIndex] as WindowTradeEvent)
    }
  }
}

function latestPricedWindowEvent(
  index: WindowTapeIndex,
  nowSec: number,
  snapshot: PriceWindowSnapshot
): WindowTradeEvent | null {
  for (let chunkIndex = index.chunks.length - 1; chunkIndex >= 0; chunkIndex--) {
    const chunk = index.chunks[chunkIndex] as WindowTapeChunk
    for (let eventIndex = firstAfter(chunk.ts, nowSec) - 1; eventIndex >= 0; eventIndex--) {
      const event = chunk.events[eventIndex] as WindowTradeEvent
      if (finitePositive(windowPrice(event, snapshot))) return event
    }
  }
  return null
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: reverse predecessor lookup must preserve native/USD companion semantics
function latestCompanionWindowEvent(
  index: WindowTapeIndex,
  nowSec: number,
  current: WindowTradeEvent,
  snapshot: PriceWindowSnapshot
): WindowTradeEvent | null {
  for (let chunkIndex = index.chunks.length - 1; chunkIndex >= 0; chunkIndex--) {
    const chunk = index.chunks[chunkIndex] as WindowTapeChunk
    for (let eventIndex = firstAfter(chunk.ts, nowSec) - 1; eventIndex >= 0; eventIndex--) {
      const event = chunk.events[eventIndex] as WindowTradeEvent
      if (compareWindowTrades(event, current) > 0) continue
      if (!finitePositive(windowPrice(event, snapshot))) continue
      if (snapshot.pct_denom === 'native' && finitePositive(event.usd_price)) return event
      if (snapshot.pct_denom !== 'native' && finitePositive(event.native_price)) return event
    }
  }
  return null
}

function applyBoundaryTrade(next: PriceWindowSnapshot, event: WindowTradeEvent): void {
  const price = windowPrice(event, next)
  if (!finitePositive(price)) return
  if (next.pct_denom === 'native') {
    next.baseline_native_price = price as number
    if (finitePositive(event.usd_price)) next.baseline_price = event.usd_price as number
  } else {
    next.baseline_price = price as number
    if (finitePositive(event.native_price))
      next.baseline_native_price = event.native_price as number
  }
  next.baseline_source_ts = iso(event.ts)
  next.baseline_source_lt = event.lt ?? null
  next.status = 'ok'
}

function applyCurrentTrade(next: PriceWindowSnapshot, event: WindowTradeEvent): void {
  const price = windowPrice(event, next)
  if (!finitePositive(price)) return
  if (next.pct_denom === 'native') {
    next.current_native_price = price as number
    next.current_price = finitePositive(event.usd_price) ? (event.usd_price as number) : null
  } else {
    next.current_price = price as number
    if (finitePositive(event.native_price)) next.current_native_price = event.native_price as number
  }
  next.current_source_ts = iso(event.ts)
  next.current_source_lt = event.lt ?? null
}

function windowBasisPrices(snapshot: PriceWindowSnapshot): {
  baseline: number | null
  current: number | null
} {
  if (snapshot.pct_denom === 'native') {
    return {
      baseline: snapshot.baseline_native_price ?? null,
      current: snapshot.current_native_price ?? null,
    }
  }
  return { baseline: snapshot.baseline_price ?? null, current: snapshot.current_price ?? null }
}

function windowPrice(event: WindowTradeEvent, snapshot: PriceWindowSnapshot): number | null {
  if (snapshot.pct_denom === 'native') {
    return finitePositive(event.native_price) ? (event.native_price as number) : null
  }
  return finitePositive(event.usd_price) ? (event.usd_price as number) : null
}

export function candlesFromWindowTrades(
  historical: Candle[],
  tape: readonly WindowTradeEvent[],
  resolution: Resolution,
  denom: Denom,
  replaceFrom?: number,
  unavailableIntervals: readonly DataAvailabilityInterval[] = []
): Candle[] {
  // Preserve both array and candle identity until a factual trade changes a
  // bucket. mergeTradeIntoCandles is immutable, so the historical seed is
  // never mutated and an unusable event becomes a true no-op for the chart.
  let candles =
    replaceFrom == null
      ? historical
      : historical.filter(
          (candle) =>
            candle.t < replaceFrom || (candle.provenance === 'pool_birth' && candle.tx_count === 0)
        )
  candles = removeSupersededBirth(candles)
  for (const event of [...tape].sort(compareWindowTrades)) {
    if (replaceFrom != null && event.ts < replaceFrom) continue
    const price = denom === 'usd' ? event.usd_price : event.native_price
    const volume = denom === 'usd' ? event.quote_usd : event.quote_native
    if (!finitePositive(price) || !finiteNonNegative(volume)) continue
    const last = candles.at(-1)
    if (last && unavailableIntervals.some(({ from, to }) => last.t <= from && event.ts >= to)) {
      const first = mergeTradeIntoCandles(
        [],
        { ts: event.ts, price: price as number, quote_amount: volume as number },
        resolution
      )
      candles = [...candles, ...first]
      continue
    }
    candles = mergeTradeIntoCandles(
      candles,
      { ts: event.ts, price: price as number, quote_amount: volume as number },
      resolution
    )
  }
  return candles
}

function removeSupersededBirth(candles: Candle[]): Candle[] {
  // A reconnect can retain the old standalone ADD next to the refreshed
  // factual prefix. The refreshed first candle already owns its birth open.
  if (candles[0]?.provenance !== 'pool_birth' || !candles.some((candle) => candle.tx_count > 0)) {
    return candles
  }
  return candles.filter((candle) => candle.provenance !== 'pool_birth' || candle.tx_count > 0)
}

export function exactTradeTailFrom(nowSec: number, resolution: Resolution): number {
  const cutoff = nowSec - 86_400
  return bucketStart(cutoff, resolution) + bucketSeconds(resolution)
}

export function tradeBelongsToCandle(
  event: WindowTradeEvent,
  candle: Candle,
  resolution: Resolution
): boolean {
  const price = event.native_price
  return (
    bucketStart(event.ts, resolution) === candle.t &&
    (!finitePositive(price) || ((price as number) >= candle.l && (price as number) <= candle.h))
  )
}

// The floor is honest only while at least one priced trade backs it; an
// all-unpriced window is null ("—", per the api/types volume contract), never
// a fabricated $0. Same convention as windowActivity's sumUSDVolume.
export function flooredWindowUSD(count: number, usd: number, unpriced: number): number | null {
  return count - unpriced === 0 && unpriced > 0 ? null : usd
}

export function applyWindowToToken(
  token: TokenSummary,
  nowSec: number,
  windowSec: number
): TokenSummary {
  if (!token.window_events) return token
  const window = deriveTradeWindow(token.window_events, nowSec, windowSec, token.price_window)
  const latest = window.events.at(-1)
  const valuationEdge = {
    ...(finiteNonNegative(latest?.mcap_usd) ? { mcap_usd: latest?.mcap_usd } : {}),
    ...(finiteNonNegative(latest?.fdv_usd) ? { fdv_usd: latest?.fdv_usd } : {}),
    ...(finiteNonNegative(latest?.liquidity_usd) ? { liquidity_usd: latest?.liquidity_usd } : {}),
  }
  if (token.window_events_tail) {
    // A server-capped tape is the live edge of the window, not the window
    // itself: the 24h shape stays on the MV series with the tape pinned as
    // its exact recent edge, and the seed price window ages against the
    // retained tape, so the pct keeps moving between snapshots. Counts/volume
    // come from the expiry head + tape (exact, aging); a row without a head
    // keeps the REST aggregates (WS trades increment them).
    const head = usableHead(token, windowSec)
    const counts = head ? headWindowCounts(head, token.window_events, nowSec, windowSec) : null
    const tonRow = !isNonTonQuote(token.primary_quote_token)
    return {
      ...token,
      ...(counts
        ? {
            ...(tonRow
              ? {
                  volume_24h_native: counts.volumeNative,
                  vol_recent_native: counts.recentVolumeNative,
                }
              : {}),
            volume_window_usd: flooredWindowUSD(
              counts.txns,
              counts.volumeUSD,
              counts.volumeUSDUnpriced
            ),
            vol_recent_usd: counts.recentVolumeUSD,
            txns_24h: counts.txns,
            txns_24h_buys: counts.buys,
            txns_24h_sells: counts.sells,
          }
        : {}),
      price_window: window.priceWindow ?? token.price_window,
      price_change_24h_pct: window.priceChangePct,
      sparkline: windowSparkline(
        window.events,
        window.priceWindow,
        nowSec,
        windowSec,
        token.sparkline
      ),
      ...valuationEdge,
    }
  }
  const tonRow = !isNonTonQuote(token.primary_quote_token)
  return {
    ...token,
    // volumeNative is the pool's quote units — TON only on a TON-quoted row.
    // A pool row without TON keeps its REST native zeros; quote units must
    // never land in a TON-labelled field.
    ...(tonRow
      ? {
          volume_24h_native: window.volumeNative,
          vol_recent_native: window.recentVolumeNative,
        }
      : {}),
    volume_window_usd: flooredWindowUSD(window.txns, window.volumeUSD, window.volumeUSDUnpriced),
    vol_recent_usd: window.recentVolumeUSD,
    txns_24h: window.txns,
    txns_24h_buys: window.buys,
    txns_24h_sells: window.sells,
    price_window: window.priceWindow ?? token.price_window,
    price_change_24h_pct: window.priceChangePct,
    sparkline: window.sparkline,
    ...valuationEdge,
  }
}

export function applyWindowToPulse(pulse: PulseItem, nowSec: number, windowSec: number): PulseItem {
  if (!pulse.window_events_ready) return pulse
  const window = deriveTradeWindow(pulse.window_events ?? [], nowSec, windowSec)
  const latest = window.events.at(-1)
  const valuationEdge = {
    ...(finiteNonNegative(latest?.mcap_usd) ? { mcap_usd: latest?.mcap_usd } : {}),
    ...(finiteNonNegative(latest?.fdv_usd) ? { fdv_usd: latest?.fdv_usd } : {}),
  }
  if (pulse.window_events_tail) {
    // Same capped-tape contract as tokens: the retained tape carries the
    // newest valuation edge; the window numbers come from the expiry head +
    // tape (exact, aging) or, without a head, stay on the server aggregates.
    const head = usableHead(pulse, windowSec)
    if (!head) return { ...pulse, ...valuationEdge }
    const counts = headWindowCounts(head, pulse.window_events ?? [], nowSec, windowSec)
    return {
      ...pulse,
      volume_24h_native: counts.volumeNative,
      volume_24h_usd: flooredWindowUSD(counts.txns, counts.volumeUSD, counts.volumeUSDUnpriced),
      txns_24h: counts.txns,
      txns_24h_buys: counts.buys,
      txns_24h_sells: counts.sells,
      ...valuationEdge,
    }
  }
  return {
    ...pulse,
    volume_24h_native: window.volumeNative,
    volume_24h_usd: flooredWindowUSD(window.txns, window.volumeUSD, window.volumeUSDUnpriced),
    txns_24h: window.txns,
    txns_24h_buys: window.buys,
    txns_24h_sells: window.sells,
    ...valuationEdge,
  }
}

export function applyWindowToPair(pair: PairDetail, nowSec: number, windowSec: number): PairDetail {
  if (!pair.window_events) return pair
  const key = pairWindowKey(windowSec)
  const seed = pair.price_windows?.[key]
  const window = deriveTradeWindow(pair.window_events, nowSec, windowSec, seed)
  const latest = window.events.at(-1)
  // Same floor convention as the token/pulse windows — one B1 pending frame
  // must not blank the whole 24h side to "—" for a second.
  const buyVolume = flooredWindowUSD(window.buys, window.buyUSD, window.buyUSDUnpriced)
  const sellVolume = flooredWindowUSD(window.sells, window.sellUSD, window.sellUSDUnpriced)
  return {
    ...pair,
    // Same quote-unit rule as the token row: the TON-labelled volume moves
    // only when the pair's quote IS TON; quote units live on the
    // quote-labelled field either way.
    volume_24h_native: isNonTonQuote(pair.quote_token)
      ? (pair.volume_24h_native ?? 0)
      : window.volumeNative,
    volume_24h_quote: window.volumeNative,
    volume_24h_buy_usd: buyVolume,
    volume_24h_sell_usd: sellVolume,
    txns_24h: window.txns,
    txns_24h_buys: window.buys,
    txns_24h_sells: window.sells,
    price_change_24h_pct: window.priceChangePct,
    ...(finiteNonNegative(latest?.mcap_usd) ? { mcap_usd: latest?.mcap_usd } : {}),
    ...(finiteNonNegative(latest?.fdv_usd) ? { fdv_usd: latest?.fdv_usd } : {}),
    ...(finiteNonNegative(latest?.liquidity_usd) ? { liquidity_usd: latest?.liquidity_usd } : {}),
    price_windows:
      seed && window.priceWindow
        ? { ...pair.price_windows, [key]: window.priceWindow }
        : pair.price_windows,
  }
}

export function applyAllWindowsToPair(pair: PairDetail, nowSec: number): PairDetail {
  if (!pair.window_events) return pair
  // One immutable factual tape with a persistent chunk index. Initial tape
  // hydration builds it once after the first frame; local time ticks and live
  // appends then query four windows by binary boundary rather than reducing
  // every retained event before React can paint.
  const index = windowTapeIndex(pair.window_events)
  const window = (seconds: number, seed?: PriceWindowSnapshot | null) => {
    const summary = summarizeIndexedWindow(index, nowSec - seconds, nowSec, nowSec - seconds / 4)
    const priceWindow = agePriceWindowIndexed(seed, index, nowSec, seconds)
    return { ...summary, priceWindow, priceChangePct: priceWindow?.pct ?? null }
  }
  const five = window(300, pair.price_windows?.['5m'])
  const hour = window(3600, pair.price_windows?.['1h'])
  const six = window(21_600, pair.price_windows?.['6h'])
  const day = window(86_400, pair.price_windows?.['24h'])
  const latest = latestWindowEvent(index, nowSec - 86_400, nowSec)
  const nonTon = isNonTonQuote(pair.quote_token)
  return {
    ...pair,
    // Non-TON quote units never refill the TON-labelled field.
    volume_24h_native: nonTon ? (pair.volume_24h_native ?? 0) : day.volumeNative,
    volume_24h_quote: day.volumeNative,
    volume_24h_buy_usd: day.buyUSD,
    volume_24h_sell_usd: day.sellUSD,
    txns_24h: day.txns,
    txns_24h_buys: day.buys,
    txns_24h_sells: day.sells,
    txns_24h_buys_unpriced: day.buyUSDUnpriced,
    txns_24h_sells_unpriced: day.sellUSDUnpriced,
    volume_5m_buy_usd: five.buyUSD,
    volume_5m_sell_usd: five.sellUSD,
    txns_5m_buys: five.buys,
    txns_5m_sells: five.sells,
    txns_5m_buys_unpriced: five.buyUSDUnpriced,
    txns_5m_sells_unpriced: five.sellUSDUnpriced,
    volume_1h_buy_usd: hour.buyUSD,
    volume_1h_sell_usd: hour.sellUSD,
    txns_1h_buys: hour.buys,
    txns_1h_sells: hour.sells,
    txns_1h_buys_unpriced: hour.buyUSDUnpriced,
    txns_1h_sells_unpriced: hour.sellUSDUnpriced,
    volume_6h_buy_usd: six.buyUSD,
    volume_6h_sell_usd: six.sellUSD,
    txns_6h_buys: six.buys,
    txns_6h_sells: six.sells,
    txns_6h_buys_unpriced: six.buyUSDUnpriced,
    txns_6h_sells_unpriced: six.sellUSDUnpriced,
    price_change_5m_pct: five.priceChangePct,
    price_change_1h_pct: hour.priceChangePct,
    price_change_6h_pct: six.priceChangePct,
    price_change_24h_pct: day.priceChangePct,
    ...(finiteNonNegative(latest?.mcap_usd) ? { mcap_usd: latest?.mcap_usd } : {}),
    ...(finiteNonNegative(latest?.fdv_usd) ? { fdv_usd: latest?.fdv_usd } : {}),
    ...(finiteNonNegative(latest?.liquidity_usd) ? { liquidity_usd: latest?.liquidity_usd } : {}),
    price_windows: {
      ...pair.price_windows,
      ...(five.priceWindow ? { '5m': five.priceWindow } : {}),
      ...(hour.priceWindow ? { '1h': hour.priceWindow } : {}),
      ...(six.priceWindow ? { '6h': six.priceWindow } : {}),
      ...(day.priceWindow ? { '24h': day.priceWindow } : {}),
    },
  }
}

function latestWindowEvent(
  index: WindowTapeIndex,
  cutoff: number,
  nowSec: number
): WindowTradeEvent | null {
  for (let chunkIndex = index.chunks.length - 1; chunkIndex >= 0; chunkIndex--) {
    const chunk = index.chunks[chunkIndex] as WindowTapeChunk
    const eventIndex = firstAfter(chunk.ts, nowSec) - 1
    if (eventIndex >= 0 && (chunk.ts[eventIndex] as number) > cutoff)
      return chunk.events[eventIndex] as WindowTradeEvent
  }
  return null
}

function pairWindowKey(windowSec: number): string {
  switch (windowSec) {
    case 60:
      return '1m'
    case 300:
      return '5m'
    case 3600:
      return '1h'
    case 21_600:
      return '6h'
    default:
      return '24h'
  }
}

// tradeUSDRate returns the trade's own quote→USD rate, in the one order every
// reader of a trade row shares (tonQuote.tradeRowUsdRate): its stamped
// ton_usd_rate first, price_usd / price only as the legacy recovery of a
// TON-quoted row without a stamp. That ratio is exact when price_usd was
// stamped as price * rate at emit. Recovering it against the CANDLE basis
// instead (price_usd / spot_price) inflates it by precisely the dust
// correction: measured +17% on a 10^4-nanoTON USDT fill, which is how a
// dollar-pegged asset printed a $1.17 candle. Both numerator and denominator
// must be the SAME price. A trade quoted outside TON has no such recovery —
// 0/absent stays unpriced, never TON/USD. A pending frame joins the tape
// unvalued for every quote (the valued patch identity-replaces it), so the
// rate only counts once.
export function tradeUSDRate(trade: TradeSummary): number | null {
  if (trade.mc_status === 'pending') return null
  return tradeRowUsdRate(trade)
}

function tradeQuoteUSD(trade: TradeSummary, usdRate: number | null): number | null {
  if (usdRate == null) return null
  const value = trade.quote_amount * usdRate
  return finiteNonNegative(value) ? value : null
}

// The USD twin of native_price: the CANDLE basis in dollars, not the raw fill.
export function tradeCandleUSDPrice(candlePrice: number, usdRate: number | null): number | null {
  if (usdRate == null || !finitePositive(candlePrice)) return null
  const value = candlePrice * usdRate
  return finitePositive(value) ? value : null
}

// The USD point a live trade contributes to USD-denominated series (window
// current, sparkline edge). A stamped price_usd is the execution price × the
// trade's rate — the point the series carried all along. A non-TON-quoted
// trade has no price_usd: the honest point is the CANDLE basis × the trade's
// own stamped rate — the same usd_price windowEventFromTrade merges into the
// tape. Pending frames and unpriced quotes stay null, never TON/USD.
export function tradeWindowUsdPrice(trade: TradeSummary, candlePrice?: number): number | null {
  if (finitePositive(trade.price_usd)) return trade.price_usd as number
  const candle = candlePrice ?? candleTradePrice(trade)
  return tradeCandleUSDPrice(candle, tradeUSDRate(trade))
}

function priceChange(baseline: number | null, current: number | null): number | null {
  if (!finitePositive(baseline) || !Number.isFinite(current)) return null
  return (((current as number) - (baseline as number)) / (baseline as number)) * 100
}

function finitePositive(value: number | null | undefined): boolean {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}

function finiteNonNegative(value: number | null | undefined): boolean {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
}

function parseSeconds(value: string | null | undefined): number | null {
  if (!value) return null
  const millis = Date.parse(value)
  return Number.isFinite(millis) ? Math.floor(millis / 1000) : null
}

function iso(seconds: number): string {
  return new Date(seconds * 1000).toISOString()
}
